import CoreLayout from "../../layouts/core-layout";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import React from "react";
import {
  HelpsCreateDiagName,
  HelpsCreateDiagDiscriminating,
  HelpsCreateDiagWait,
  HelpsCreateDiagHelp,
} from "../helps/contents/helps-create-diag";
import { ModalConfirm, PriceDisplay, WarningQuotas } from "../../lib/product-manager/ProductManager";
import { BankManagerAccounContext } from "../../lib/bank-manager/BankManagerAccount";
import i18n from "../../i18n";
import { getProductActions, ProductActionStatus } from "../../api/ProductManagerActionApi";
import { createBasicDiagnosisRequestEvent } from "../../../src/api/DiagnosisApi";
import { ProductActionHandler } from "../../../src/lib/product-action-handler/ProductActionHandler";
import { useAuth } from "react-oidc-context";
import { useNavigate } from "react-router-dom";
import HelpsModals from "../helps/helps-modals";
import { getPackageOrProduct } from "../../lib/product-manager/product-manager.service";
import { AttributeGroup, PackageAttribute, Product, ProductAttribute } from "../../lib/product-manager/types";
import { ProductAttributeGroupManager } from "../../lib/product-manager/product-attribute-group";

function Diagnosis() {

  const auth = useAuth()
  const [refreshCount, setRefreshCount] = useState<number>(0)
  const [inputKeyword, setInputKeyword] = useState<string>("");
  const [negativeKeyword, setNegativeKeyword] = useState<string>("")

  useEffect(() => {
    if (auth?.isAuthenticated) {
      const _inputKeyword = `${auth?.user?.profile?.given_name || ""} ${auth?.user?.profile?.family_name || ""}`
      setInputKeyword(_inputKeyword)
    }
  }, [auth])

  const texts = [
    <HelpsCreateDiagName />,
    <HelpsCreateDiagDiscriminating />,
    <HelpsCreateDiagWait />,
    <HelpsCreateDiagHelp />,
  ];

  const { t } = useTranslation();

  function getActionMetadata() {
    return {}
  }

  function getCreateDiagMetadata(keyword: string, negativeKeyword: string) {
    const data = {
      negativeKeyword,
      keyword
    };
    return data
  }


  const { account, refresh: refreshAccount } = useContext(BankManagerAccounContext)
  const [pendingAction, setPendingAction] = useState<{
    checkoutId: number;
    pendingActionCount: number;
  } | null
  >(null)
  const navigate = useNavigate()


  function handleIsActionPending(params: {
    checkoutId: number;
    pendingActionCount: number;
  }) {
    setPendingAction(params)
  }

  const [product, setProduct] = useState<(Product | null)>(null)
  const [selectedOptions, setSelectedOptions] = useState<{ groupId: number, id: number }[]>([])
  const [modalConfirmToggled, setModalConfirmToggled] = useState<boolean>(false)

  useEffect(() => {
    getPackageOrProduct('google-serp', 'Product').then((data) => data.results[0])
      .then((product: Product) => {
        setProduct(product)
        setSelectedOptions(
          product.attributeGroups.reduce((array, attributeGroup) => {
            array = [...array, ...attributeGroup.attributes.filter((attribute) => attribute.isDefault).map((attribute) => ({
              ...attribute,
              group: { id: attributeGroup.id } as AttributeGroup<PackageAttribute | ProductAttribute>
            }))]
            return array
          }, [] as ProductAttribute[])
            .map((attribute: ProductAttribute) => ({
              groupId: attribute?.group?.id as unknown as number,
              id: attribute.id as unknown as number
            }))
        )
      }
      )
  }, [refreshCount])



  return (
    <>
      <CoreLayout template="main">
        <div style={{ margin: "30px" }}></div>


        <div className="bg-body rounded shadow-sm p-10 p-lg-15 mx-auto mt-10 animated tada mb-10 w-lg-500px">
          <ProductActionHandler
            pendingActionCount={pendingAction?.pendingActionCount}
            checkoutId={pendingAction?.checkoutId}
            getActions={
              (checkoutId: number) =>
                getProductActions({ status: [ProductActionStatus.SUCCESS], checkoutId }).then((response) => {
                  return response.data.data.results
                })
            }
            onTargetedActionExists={(targetedActions) => {
              refreshAccount()
              setPendingAction(null)
              setRefreshCount((state) => state + 1)
              navigate(`/result/${targetedActions[0].metadata.diagUUID}/overview`)
            }}
          >

            <div className="form">
              <div className="mb-10 d-flex align-items-center justify-content-between">
                <HelpsModals texts={texts} />

                {product?.prices?.[0]?.value ? <PriceDisplay
                  currentLanguage={i18n.language || 'fr'}
                  value={product?.prices?.[0]?.value || 0}
                  creditSystemEnabled={process.env.REACT_APP_CREDIT_SYSTEM_ENABLED === '1'}
                /> : null}

              </div>
              <div className="text-center mb-10">
                <h1 className="text-dark mb-3">
                  {t("core:pages.osculteo.DiagnosisWithNegativeKeyword.title")}
                </h1>
              </div>
              <div className="mb-10">
                {t("core:pages.osculteo.DiagnosisWithNegativeKeyword.text")}
              </div>
              <div className="fv-row mb-10">
                <label className="form-label fs-6 fw-bolder text-dark">
                  {t(
                    "core:pages.osculteo.DiagnosisWithNegativeKeyword.forms.input.keyword.label"
                  )}
                </label>
                <input
                  required
                  className="form-control form-control-lg form-control-solid"
                  type="text"
                  id="keyword"
                  placeholder={"Exemple : Prénom Nom"}
                  autoFocus
                  onChange={(event) => {
                    setInputKeyword(event.target.value)
                  }}
                  value={inputKeyword}
                />

              </div>
              <div className="fv-row mb-10">
                <label className="form-label fs-6 fw-bolder text-dark">
                  {t(
                    "core:pages.osculteo.DiagnosisWithNegativeKeyword.forms.input.negativeKeyword.label"
                  )}
                </label>
                <input
                  className="form-control form-control-lg form-control-solid"
                  type="text"
                  id="negativeKeyword"
                  placeholder={t(
                    "core:pages.osculteo.DiagnosisWithNegativeKeyword.forms.input.negativeKeyword.placeholder"
                  )}
                  autoComplete="negativeKeyword"
                  onChange={(event) => {
                    setNegativeKeyword(event.target.value)
                  }}
                  value={negativeKeyword}
                  autoFocus
                />

              </div>

              <div className="fv-row mb-10">
                {product && product.attributeGroups.length ?
                  <ProductAttributeGroupManager
                    currentLanguage={i18n.language || 'fr'}
                    setSelectedOptions={setSelectedOptions}
                    selectedOptions={selectedOptions}
                    groups={product.attributeGroups}
                    creditSystemEnabled={process.env.REACT_APP_CREDIT_SYSTEM_ENABLED === '1'}
                  /> : null

                }
              </div>

              <div className="text-center mt-4">
                <button
                  className={`btn btn-lg ${!product || !product.isAvailable ? 'btn-secondary' : 'btn-primary'} w-100 mb-5`}
                  disabled={!product || !product.isAvailable}
                  onClick={() => setModalConfirmToggled(true)}
                >
                  {t(
                    "core:pages.osculteo.DiagnosisWithNegativeKeyword.forms.button"
                  )}

                </button>
              </div>

              {!product?.isAvailable ? <WarningQuotas products={[product as Product]} currentLanguage={i18n.language || 'fr'} /> : null}

            </div>


            <ModalConfirm
              creditSystemEnabled={process.env.REACT_APP_CREDIT_SYSTEM_ENABLED === '1'}
              createNewDiagnosis={(params) => createBasicDiagnosisRequestEvent(params).then(({ data }) => data.data.uuid)}
              loadingImageBaneerSrc={`${process.env.REACT_APP_CDN_PLATFORMS}/slider-1.jpg`}
              currentLanguage={i18n.language as 'fr' | 'en'}
              userBalance={process.env.REACT_APP_CREDIT_SYSTEM_ENABLED === "1" ? account?.balance || 0 : null}
              getCreateDiagMetadata={() => getCreateDiagMetadata(inputKeyword, negativeKeyword)}
              getActionMetadata={getActionMetadata}
              isActionPending={pendingAction ? true : false}
              setIsActionPending={handleIsActionPending}
              selectedItems={product && selectedOptions ? [
                { ...product, selectedProductAttributesIds: selectedOptions.map((selectedOption) => selectedOption.id) }
              ] : []}
              setShow={setModalConfirmToggled}
              show={modalConfirmToggled}
            />

          </ProductActionHandler>

        </div>
      </CoreLayout >
    </>
  );
};

export default Diagnosis;
